/**
 * HarnessWoundSystem — tracks Basil's pneumatic-harness integrity.
 *
 * Canon spec: `docs/specs/SPEC-wounds-and-medicine.md`.
 *
 * Composes WITH `EnergySystem` — it does NOT replace the Kinetic Battery.
 * `EnergySystem` answers "can you fire right now?" and drives passive
 * recharge + depleted penalties. `HarnessWoundSystem` answers "what does
 * firing feel like?" and tunes individual Basil abilities (Turn-Table
 * Touch, Overclocked Leap, Pneumatic Anchor, Resonant Drop / shockwaves)
 * via per-state multipliers sourced from {@link HARNESS.STATES}.
 *
 * Four states mapped by integrity thresholds (0-100):
 *   intact  (76-100) — default, no signature
 *   cracked (41-75)  — hairline cracks, wisps of steam
 *   venting (11-40)  — audible hiss, copper sparks, TTT window widens
 *   limping ( 0-10)  — near-failure, heavy systems offline
 *
 * Damage sources (applied via `applyDamage`):
 *   - Fall damage (scene's fall-speed threshold, wired in PlayerEntity)
 *   - NPC hit (Fox contact in L2/L3)
 *   - Failed rhythm (Venting only — missed TTT sharpens the drain)
 *
 * Healing sources (applied via `heal`):
 *   - Minna's Purr — +8 integrity/sec while within purr radius
 *   - Grease Cache pickup — +25 burst
 *   - Elder encounters — full restoration
 *   - Passive idle regen — +1/sec after 3s of no damage
 *
 * Events emitted (see `config/events.ts`):
 *   - HARNESS_STATE_CHANGED — threshold crossings only
 *   - HARNESS_INTEGRITY_CHANGED — every change (HUD smoothing)
 *   - HARNESS_DAMAGED / HARNESS_HEALED — one-shot edge events
 */

import { gameEvents } from '../core/EventBus';
import { GameEvents } from '../config/events';
import { HARNESS, type HarnessState } from '../config/GameConfig';
import { evaluateFail } from './flow/FailEvaluator';

export interface HarnessWoundConfig {
  /** Maximum integrity capacity. Defaults to `HARNESS.MAX`. */
  maxIntegrity?: number;
  /** Character identifier emitted with events (always "basil" today). */
  character?: string;
  /** Override passive-regen rate (units/sec). Defaults to `HARNESS.HEAL.PASSIVE_IDLE_PER_SEC`. */
  passiveRegenPerSec?: number;
  /** Override seconds after damage before passive regen resumes. */
  passiveRegenDelay?: number;
}

export class HarnessWoundSystem {
  current: number;
  readonly max: number;
  readonly character: string;

  private readonly passiveRegenPerSec: number;
  private readonly passiveRegenDelay: number;

  /** Seconds since last damage; gates passive regen. */
  private timeSinceDamage = Infinity;

  /** Cached state; re-computed on every delta so event emits are cheap. */
  private _state: HarnessState;

  constructor(config: HarnessWoundConfig = {}) {
    this.max = config.maxIntegrity ?? HARNESS.MAX;
    this.current = this.max;
    this.character = config.character ?? 'basil';
    this.passiveRegenPerSec =
      config.passiveRegenPerSec ?? HARNESS.HEAL.PASSIVE_IDLE_PER_SEC;
    this.passiveRegenDelay =
      config.passiveRegenDelay ?? HARNESS.PASSIVE_REGEN_DELAY;
    this._state = HarnessWoundSystem.computeState(this.current);
  }

  // ── Public API ────────────────────────────────────────────────────

  /** Current harness state ({@link HarnessState}). */
  getState(): HarnessState {
    return this._state;
  }

  /** Current integrity as a 0-1 fraction (for HUD). */
  get fraction(): number {
    return this.max > 0 ? this.current / this.max : 0;
  }

  /** True when the harness is at or below Limping. */
  get isLimping(): boolean {
    return this._state === 'limping';
  }

  /** True when heavy abilities are locked out (spec: Limping only). */
  get heavyAbilitiesLockedOut(): boolean {
    return this._state === 'limping';
  }

  /** Per-state tuning table for the current state. */
  get tuning(): (typeof HARNESS.STATES)[keyof typeof HARNESS.STATES] {
    switch (this._state) {
      case 'intact':
        return HARNESS.STATES.INTACT;
      case 'cracked':
        return HARNESS.STATES.CRACKED;
      case 'venting':
        return HARNESS.STATES.VENTING;
      case 'limping':
        return HARNESS.STATES.LIMPING;
    }
  }

  /**
   * Deal damage to the harness. Returns the actual damage applied
   * (clamped to current integrity).
   *
   * @param amount positive damage value; values ≤ 0 are ignored.
   * @param source free-form string used for telemetry + banter hooks.
   */
  applyDamage(amount: number, source = 'unknown'): number {
    if (!Number.isFinite(amount) || amount <= 0) return 0;
    if (this.current <= 0) return 0;

    const prevState = this._state;
    const prev = this.current;
    this.current = Math.max(0, this.current - amount);
    const applied = prev - this.current;
    this.timeSinceDamage = 0;

    // Update state FIRST so the *_CHANGED + DAMAGED events carry the
    // post-hit state (matches how test fixtures and HUD listeners read
    // the payload — they expect `state` to reflect the new integrity).
    this.recomputeState(prevState, `damage:${source}`);
    this.emitIntegrityChanged();
    gameEvents.emit(GameEvents.HARNESS_DAMAGED, {
      amount: applied,
      source,
      integrity: this.current,
      state: this._state,
    });

    // Phase 5 light-fail: integrity reaching zero is a recoverable puzzle
    // fail. `prev > 0 && current === 0` makes this fire exactly once on the
    // crossing (the `current <= 0` guard at the top of applyDamage already
    // ignores re-hits once at zero, so a second emit is impossible here).
    if (prev > 0 && this.current === 0) {
      // evaluateFail always returns 'harness' here: harnessIntegrity===0 and
      // escortLost===false, so the harness branch is the only reachable path.
      if (evaluateFail({ harnessIntegrity: this.current, escortLost: false }) === 'harness') {
        gameEvents.emit(GameEvents.PUZZLE_FAILED, {
          puzzleId: 'harness',
          reason: 'harness',
        });
      }
    }
    return applied;
  }

  /**
   * Heal the harness by the given amount. Returns the actual amount
   * applied (clamped to max).
   *
   * @param amount positive heal value; values ≤ 0 are ignored.
   * @param source free-form source string (purr, greaseCache, elder, idle).
   */
  heal(amount: number, source = 'unknown'): number {
    if (!Number.isFinite(amount) || amount <= 0) return 0;
    if (this.current >= this.max) return 0;

    const prevState = this._state;
    const prev = this.current;
    this.current = Math.min(this.max, this.current + amount);
    const applied = this.current - prev;
    if (applied <= 0) return 0;

    // Update state FIRST so event payloads carry the post-heal state
    // (matches applyDamage ordering above).
    this.recomputeState(prevState, `heal:${source}`);
    this.emitIntegrityChanged();
    gameEvents.emit(GameEvents.HARNESS_HEALED, {
      amount: applied,
      source,
      integrity: this.current,
      state: this._state,
    });
    return applied;
  }

  /**
   * Restore integrity to full (Elder cutscenes).
   */
  fullHeal(source = 'elder'): number {
    return this.heal(this.max, source);
  }

  /**
   * Per-frame update. Advances the damage-dead-time timer and applies
   * passive idle regen once {@link passiveRegenDelay} has elapsed.
   */
  update(dt: number): void {
    if (!Number.isFinite(dt) || dt <= 0) return;
    this.timeSinceDamage += dt;
    if (
      this.current < this.max &&
      this.timeSinceDamage >= this.passiveRegenDelay &&
      this.passiveRegenPerSec > 0
    ) {
      this.heal(this.passiveRegenPerSec * dt, 'idle');
    }
  }

  // ── Internal ──────────────────────────────────────────────────────

  /**
   * Pure helper: map an integrity scalar to a {@link HarnessState} using
   * the canonical thresholds from `HARNESS.THRESHOLDS`.
   */
  static computeState(integrity: number): HarnessState {
    if (integrity >= HARNESS.THRESHOLDS.INTACT_MIN) return 'intact';
    if (integrity >= HARNESS.THRESHOLDS.CRACKED_MIN) return 'cracked';
    if (integrity >= HARNESS.THRESHOLDS.VENTING_MIN) return 'venting';
    return 'limping';
  }

  private recomputeState(prevState: HarnessState, reason: string): void {
    const nextState = HarnessWoundSystem.computeState(this.current);
    if (nextState === prevState) return;
    this._state = nextState;
    gameEvents.emit(GameEvents.HARNESS_STATE_CHANGED, {
      prev: prevState,
      next: nextState,
      reason,
      integrity: this.current,
    });
  }

  private emitIntegrityChanged(): void {
    gameEvents.emit(GameEvents.HARNESS_INTEGRITY_CHANGED, {
      current: this.current,
      max: this.max,
      state: this._state,
    });
  }
}
